import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils';

interface PopoverProps {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  triggerClassName?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: 'click' | 'hover';
  delay?: number;
}

const Popover: React.FC<PopoverProps> = ({
  content,
  children,
  className,
  triggerClassName,
  side = 'bottom',
  align = 'start',
  open: controlledOpen,
  onOpenChange,
  trigger = 'click',
  delay = 150,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const [timer, setTimer] = useState<number | null>(null);

  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const [actualSide, setActualSide] = useState<PopoverProps['side']>(side);

  const setOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };

  const toggleOpen = () => setOpen(!open);

  const handleMouseEnter = () => {
    if (trigger !== 'hover') return;
    if (timer) window.clearTimeout(timer);
    const t = window.setTimeout(() => setOpen(true), delay);
    setTimer(t);
  };

  const handleMouseLeave = () => {
    if (trigger !== 'hover') return;
    if (timer) window.clearTimeout(timer);
    setOpen(false);
  };

  const GAP = 8;

  const computePosition = () => {
    if (!triggerRef.current || !popoverRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const pop = popoverRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;

    let nextSide: PopoverProps['side'] = side;
    let top = 0;
    let left = 0;

    const placeHorizontal = () => {
      if (align === 'start') left = rect.left;
      else if (align === 'end') left = rect.right - pw;
      else left = rect.left + rect.width / 2 - pw / 2;
      left = Math.max(8, Math.min(left, vw - pw - 8));
    };

    const placeVertical = () => {
      if (align === 'start') top = rect.top;
      else if (align === 'end') top = rect.bottom - ph;
      else top = rect.top + rect.height / 2 - ph / 2;
      top = Math.max(8, Math.min(top, vh - ph - 8));
    };

    if (side === 'top') {
      top = rect.top - ph - GAP;
      placeHorizontal();
      if (top < 4) {
        nextSide = 'bottom';
        top = rect.bottom + GAP;
        placeHorizontal();
      }
    } else if (side === 'bottom') {
      top = rect.bottom + GAP;
      placeHorizontal();
      if (top + ph > vh - 4) {
        nextSide = 'top';
        top = rect.top - ph - GAP;
        placeHorizontal();
      }
    } else if (side === 'left') {
      left = rect.left - pw - GAP;
      placeVertical();
      if (left < 4) {
        nextSide = 'right';
        left = rect.right + GAP;
        placeVertical();
      }
    } else if (side === 'right') {
      left = rect.right + GAP;
      placeVertical();
      if (left + pw > vw - 4) {
        nextSide = 'left';
        left = rect.left - pw - GAP;
        placeVertical();
      }
    }

    setActualSide(nextSide);
    setCoords({ top: Math.round(top), left: Math.round(left) });
  };

  useEffect(() => {
    if (!open) return;
    computePosition();
    const handle = () => computePosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, align]);

  // Close on click outside (only for click trigger)
  useEffect(() => {
    if (!open || trigger !== 'click') return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, trigger]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  return (
    <span
      className={cn('relative inline-flex', triggerClassName)}
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        onClick={trigger === 'click' ? toggleOpen : undefined}
        className={trigger === 'click' ? 'cursor-pointer' : ''}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            onMouseEnter={trigger === 'hover' ? handleMouseEnter : undefined}
            onMouseLeave={trigger === 'hover' ? handleMouseLeave : undefined}
            className={cn(
              'fixed z-[9999] rounded-lg bg-white border border-gray-300 shadow-xl text-sm',
              className
            )}
            style={coords ? { top: coords.top, left: coords.left } : undefined}
            role="dialog"
            aria-modal="false"
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  );
};

export default Popover;
